import { createContext, useRef, useState } from 'react';
import { Popover } from '../popover/Popover';
import { MoreMenuButton as BaseMoreMenuButton } from './MoreMenuButton';
import {
  focusFirstMoreMenuItem,
  handleMoreMenuPopoverKeyDown,
} from './moreMenuPopoverKeyboard';

interface MoreMenuButtonWithPopoverProps {
  menuItems: React.ReactNode;
  tabIndex?: number;
  onOpenChange?: (open: boolean) => void;
}

export const MenuCloseContext = createContext<(() => void) | null>(null);

export const MoreMenuButtonWithPopover = ({
  menuItems,
  tabIndex,
  onOpenChange,
}: MoreMenuButtonWithPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const setOpenState = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const handleMoreMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpenState(!open);
  };

  const closeMenu = () => setOpenState(false);

  const focusFirstMenuItem = () => {
    focusFirstMoreMenuItem(menuRef.current);
  };

  const handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    handleMoreMenuPopoverKeyDown(event, anchorEl);
  };

  return (
    <>
      <BaseMoreMenuButton onClick={handleMoreMenuClick} tabIndex={tabIndex} />
      <Popover
        id="more-menu-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleMoreMenuClick}
        slotProps={{
          transition: {
            onEntered: focusFirstMenuItem,
          },
        }}
        boxSxProps={{
          p: 0,
          py: 1,
        }}
      >
        {/** biome-ignore lint/a11y/noStaticElementInteractions: Because it is a presentation component only for accessability*/}
        <div ref={menuRef} role="presentation" onKeyDown={handlePopoverKeyDown}>
          <MenuCloseContext.Provider value={closeMenu}>
            {menuItems}
          </MenuCloseContext.Provider>
        </div>
      </Popover>
    </>
  );
};
